/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * How a property is named wherever it is referenced by id.
 *
 * Extracted from the visits property picker so the leases list names the
 * same property the same way; a second implementation would drift, and a
 * broker seeing two names for one property is worse than seeing the id.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

/**
 * The property's own name, when it has a usable one.
 *
 * `description.headline` is what the product calls the property
 * everywhere a person reads it. Headlines built from missing data come
 * out starting with "undefined", so those are refused rather than shown.
 */
function propertyName(p: any): string {
  const headline: string = p?.description?.headline ?? '';
  return headline && !headline.startsWith('undefined') ? headline : '';
}

/**
 * Display label from the property address parts.
 */
function labelFromAddress(loc: any): string {
  const parts = [loc?.street, loc?.number, loc?.neighborhood].filter(Boolean);
  return parts.join(', ');
}

/**
 * Context label from property type, typology and city.
 */
function labelFromContext(p: any, loc: any): string {
  const parts: string[] = [];
  if (p.property_type) parts.push(p.property_type);
  if (p.transaction_type) parts.push(p.transaction_type);
  if (p.description?.typology) parts.push(p.description.typology);
  if (loc?.city) parts.push(loc.city);
  return parts.join(' · ');
}

/**
 * Truncated id, as a last-resort label.
 */
function labelFromId(p: any): string {
  const id = p.id || '';
  return id.length > 8 ? `Imóvel ${id.slice(0, 8)}…` : `Imóvel ${id}`;
}

/**
 * Best available human-readable label for a property.
 *
 * The property's name comes first. The address is only a fallback, and a
 * poor one: records seeded or imported without a real address still carry
 * a filled-in `location`, so an address-first order confidently prints
 * "Rua não informada, S/N, Camorim" over a perfectly good name.
 *
 * @param p - The raw property record.
 * @returns The best available display label.
 */
export function formatPropertyLabel(p: any): string {
  const candidates = [
    propertyName(p),
    labelFromAddress(p?.location),
    labelFromContext(p ?? {}, p?.location),
  ];
  return candidates.find(Boolean) || labelFromId(p ?? {});
}

/**
 * Non-empty type/typology/location parts for the sublabel.
 */
function sublabelParts(p: any): string[] {
  return [
    p?.property_type,
    p?.description?.typology,
    p?.location?.neighborhood,
    p?.location?.city,
  ].filter(Boolean);
}

/**
 * Concise sublabel with property type, typology and location.
 *
 * @param p - The raw property record.
 * @returns The sublabel text, falling back to the display label.
 */
export function buildPropertySublabel(p: any): string {
  const parts = sublabelParts(p);
  return parts.length > 0 ? parts.join(' · ') : formatPropertyLabel(p);
}
